import React, { useMemo, useState } from 'react';
import { api } from '../api.js';
import { Card, ErrorBanner, Loading, Modal, useAsync, useSession, useToast } from '../ui.jsx';
import { centsToInput, money, monthLabel, nameOf, parseAmountToCents, splitEvenly, unitName } from '../lib/format.js';
import { Link } from '../main.jsx';

const today = () => new Date(Date.now() + 8 * 3600 * 1000).toISOString().slice(0, 10);

/**
 * 循環設定 generate normal 付款項目 through the Cloudflare Cron Trigger. A run or a
 * retry of the same occurrence never generates the same 付款項目 twice, and editing
 * a setting only affects occurrences that have not been generated yet.
 */
export default function RecurrencesPage() {
  const session = useSession();
  const notify = useToast();
  const recurrences = useAsync(() => api.recurrences(), []);
  const [dialog, setDialog] = useState(null);
  const [error, setError] = useState(null);
  const [runResult, setRunResult] = useState(null);

  if (recurrences.loading) return <Loading />;
  if (recurrences.error) return <ErrorBanner error={recurrences.error} />;

  const act = async (action, message) => {
    setError(null);
    try {
      const result = await action();
      notify(message);
      setDialog(null);
      recurrences.reload();
      return result;
    } catch (failure) {
      setError(failure);
      return null;
    }
  };

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <div className="eyebrow">帳簿控制台</div>
          <h1>循環設定</h1>
          <p className="small muted">按發出日及到期日定期產生固定金額付款項目；排程重試不會產生重複項目。</p>
        </div>
        <div className="actions">
          <button
            type="button"
            data-testid="recurrence-run"
            onClick={async () => {
              const result = await act(() => api.runRecurrences({}), '已執行循環設定產生');
              if (result) setRunResult(result);
            }}
          >
            立即執行排程
          </button>
          <button type="button" className="primary" onClick={() => setDialog({ kind: 'create' })} data-testid="recurrence-create">新增循環設定</button>
        </div>
      </div>

      <ErrorBanner error={error} />

      {runResult ? (
        <div className="banner info small" data-testid="recurrence-run-result">
          以 {runResult.as_of} 執行：新產生 {runResult.generated.length} 個付款項目，
          略過（已產生／重試）{runResult.skipped.length} 個 occurrence。
        </div>
      ) : null}

      {recurrences.data.recurrences.length === 0 ? (
        <Card testId="recurrence-empty"><p className="small muted">尚未建立循環設定。</p></Card>
      ) : null}

      {recurrences.data.recurrences.map((recurrence) => (
        <Card
          key={recurrence.id}
          title={recurrence.title_template}
          subtitle={`${unitName(session.all_units, recurrence.unit_id)} · ${recurrence.category} · ${money(recurrence.total_cents)} · 每月 ${recurrence.issue_day} 日發出，${recurrence.due_month_offset ? '下月' : '同月'} ${recurrence.due_day} 日到期`}
          testId={`recurrence-${recurrence.id}`}
          actions={(
            <div className="actions">
              <span className={`chip ${recurrence.active ? 'settled' : 'cancelled'}`} data-testid={`recurrence-status-${recurrence.id}`}>
                {recurrence.active ? '生效中' : '已停用'}
              </span>
              <button type="button" className="small" onClick={() => setDialog({ kind: 'edit', recurrence })} data-testid={`recurrence-edit-${recurrence.id}`}>修改</button>
              {recurrence.active ? (
                <button
                  type="button"
                  className="small"
                  data-testid={`recurrence-disable-${recurrence.id}`}
                  onClick={() => act(() => api.disableRecurrence(recurrence.id, { reason: '不再需要' }), '循環設定已停用')}
                >
                  停用
                </button>
              ) : null}
            </div>
          )}
        >
          <div className="small muted">
            期間 {monthLabel(recurrence.start_month)} 起{recurrence.end_month ? ` 至 ${monthLabel(recurrence.end_month)}` : ''} ·
            收款人 {recurrence.recipient_kind === 'member' ? nameOf(session.members, recurrence.recipient_member_id) : recurrence.recipient_name} ·
            確認人 {nameOf(session.members, recurrence.confirmer_member_id)}
          </div>
          <div className="table-wrap" style={{ marginTop: 10 }}>
            <table>
              <thead><tr><th>成員</th><th className="num">分攤</th></tr></thead>
              <tbody>
                {recurrence.allocations.map((allocation) => (
                  <tr key={allocation.member_id}>
                    <td>{nameOf(session.members, allocation.member_id)}</td>
                    <td className="num">{money(allocation.amount_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="small" style={{ marginTop: 10 }}>
            <strong>已產生 occurrence（{recurrence.runs.length}）</strong>
            <ul className="audit-list">
              {recurrence.runs.map((run) => (
                <li key={run.occurrence_key} data-testid={`occurrence-${run.occurrence_key}`}>
                  {run.occurrence_key} · <Link to={`/items/${run.item_id}`}>付款項目</Link>
                </li>
              ))}
            </ul>
          </div>
        </Card>
      ))}

      {dialog ? (
        <RecurrenceModal
          recurrence={dialog.recurrence}
          onClose={() => setDialog(null)}
          onSubmit={(payload) => act(
            () => (dialog.recurrence ? api.updateRecurrence(dialog.recurrence.id, payload) : api.createRecurrence(payload)),
            dialog.recurrence ? '循環設定已修改（只影響尚未產生的 occurrence）' : '循環設定已建立',
          )}
        />
      ) : null}
    </div>
  );
}

function RecurrenceModal({ recurrence, onClose, onSubmit }) {
  const session = useSession();
  const payingMembers = session.members.filter((member) => member.status === 'active');
  const [form, setForm] = useState(() => ({
    unit_id: recurrence ? recurrence.unit_id : (session.all_units[0] ? session.all_units[0].id : ''),
    title_template: recurrence ? recurrence.title_template : '{月}租金',
    category: recurrence ? recurrence.category : '租金',
    total: centsToInput(recurrence ? recurrence.total_cents : 0),
    issue_day: recurrence ? recurrence.issue_day : 1,
    due_day: recurrence ? recurrence.due_day : 5,
    due_month_offset: recurrence ? recurrence.due_month_offset : 0,
    start_month: recurrence ? recurrence.start_month : today().slice(0, 7),
    end_month: recurrence && recurrence.end_month ? recurrence.end_month : '',
    recipient_kind: recurrence ? recurrence.recipient_kind : 'external',
    recipient_member_id: recurrence && recurrence.recipient_member_id ? recurrence.recipient_member_id : payingMembers[0].id,
    recipient_name: recurrence && recurrence.recipient_name ? recurrence.recipient_name : '',
    confirmer_member_id: recurrence ? recurrence.confirmer_member_id : '',
    reason: '',
  }));
  const [selected, setSelected] = useState(() => (recurrence
    ? recurrence.allocations.map((allocation) => allocation.member_id)
    : payingMembers.map((member) => member.id)));

  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const totalCents = parseAmountToCents(form.total);
  const allocations = useMemo(() => {
    if (totalCents === null) return [];
    const shares = splitEvenly(totalCents, selected.length);
    return selected.map((memberId, index) => ({ member_id: memberId, amount_cents: shares[index] }));
  }, [totalCents, selected]);

  const confirmerCandidates = payingMembers.filter((member) => !selected.includes(member.id));
  const valid = totalCents !== null && totalCents > 0 && selected.length > 0 && form.title_template.trim()
    && (form.recipient_kind === 'member' || (form.recipient_name.trim() && form.confirmer_member_id));

  return (
    <Modal title={recurrence ? '修改循環設定' : '新增循環設定'} subtitle="修改只影響尚未產生的 occurrence" onClose={onClose} wide>
      <div className="grid form">
        <label className="field">
          <span>單位</span>
          <select value={form.unit_id} onChange={(event) => update('unit_id', event.target.value)} data-testid="recurrence-unit">
            {session.all_units.map((unit) => <option key={unit.id} value={unit.id}>{unit.name}</option>)}
          </select>
        </label>
        <label className="field">
          <span>分類</span>
          <select value={form.category} onChange={(event) => update('category', event.target.value)} data-testid="recurrence-category">
            {session.categories.map((category) => <option key={category} value={category}>{category}</option>)}
          </select>
        </label>
        <label className="field" style={{ gridColumn: '1 / -1' }}>
          <span>項目名稱樣式（可用 {'{月}'} 代入月份）</span>
          <input value={form.title_template} onChange={(event) => update('title_template', event.target.value)} data-testid="recurrence-title" />
        </label>
        <label className="field"><span>固定金額（HKD）</span><input value={form.total} onChange={(event) => update('total', event.target.value)} data-testid="recurrence-total" /></label>
        <label className="field"><span>每月發出日</span><input type="number" min="1" max="28" value={form.issue_day} onChange={(event) => update('issue_day', Number(event.target.value))} data-testid="recurrence-issue-day" /></label>
        <label className="field"><span>到期日</span><input type="number" min="1" max="28" value={form.due_day} onChange={(event) => update('due_day', Number(event.target.value))} data-testid="recurrence-due-day" /></label>
        <label className="field">
          <span>到期月份</span>
          <select value={form.due_month_offset} onChange={(event) => update('due_month_offset', Number(event.target.value))} data-testid="recurrence-due-offset">
            <option value={0}>同月</option>
            <option value={1}>下一個月</option>
          </select>
        </label>
        <label className="field"><span>開始月份</span><input type="month" value={form.start_month} onChange={(event) => update('start_month', event.target.value)} data-testid="recurrence-start" /></label>
        <label className="field"><span>結束月份（選填）</span><input type="month" value={form.end_month} onChange={(event) => update('end_month', event.target.value)} data-testid="recurrence-end" /></label>
        <label className="field">
          <span>收款人類型</span>
          <select value={form.recipient_kind} onChange={(event) => update('recipient_kind', event.target.value)} data-testid="recurrence-recipient-kind">
            <option value="external">群組以外</option>
            <option value="member">群組成員</option>
          </select>
        </label>
        {form.recipient_kind === 'member' ? (
          <label className="field">
            <span>收款成員</span>
            <select value={form.recipient_member_id} onChange={(event) => update('recipient_member_id', event.target.value)} data-testid="recurrence-recipient-member">
              {payingMembers.map((member) => <option key={member.id} value={member.id}>{member.display_name}</option>)}
            </select>
          </label>
        ) : (
          <>
            <label className="field"><span>外部收款人</span><input value={form.recipient_name} onChange={(event) => update('recipient_name', event.target.value)} data-testid="recurrence-recipient-name" /></label>
            <label className="field">
              <span>確認人（非付款成員）</span>
              <select value={form.confirmer_member_id} onChange={(event) => update('confirmer_member_id', event.target.value)} data-testid="recurrence-confirmer">
                <option value="">請選擇</option>
                {confirmerCandidates.map((member) => <option key={member.id} value={member.id}>{member.display_name}</option>)}
              </select>
            </label>
          </>
        )}
      </div>

      <fieldset style={{ marginTop: 12 }}>
        <legend>分攤成員</legend>
        <div className="stack">
          {payingMembers.map((member) => (
            <label className="row small" key={member.id} style={{ justifyContent: 'space-between' }}>
              <span className="row" style={{ gap: 6 }}>
                <input
                  type="checkbox"
                  style={{ width: 'auto' }}
                  checked={selected.includes(member.id)}
                  data-testid={`recurrence-member-${member.display_name}`}
                  onChange={(event) => setSelected(event.target.checked
                    ? [...selected, member.id]
                    : selected.filter((id) => id !== member.id))}
                />
                {member.display_name}
              </span>
              <span className="amount">
                {money((allocations.find((allocation) => allocation.member_id === member.id) || {}).amount_cents ?? 0)}
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      {recurrence ? (
        <label className="field" style={{ marginTop: 12 }}>
          <span>修改原因（選填）</span>
          <input value={form.reason} onChange={(event) => update('reason', event.target.value)} data-testid="recurrence-reason" />
        </label>
      ) : null}

      <div className="modal-actions">
        <button type="button" onClick={onClose}>取消</button>
        <button
          type="button"
          className="primary"
          disabled={!valid}
          data-testid="recurrence-save"
          onClick={() => onSubmit({
            unit_id: form.unit_id,
            title_template: form.title_template.trim(),
            category: form.category,
            total_cents: totalCents,
            allocations,
            recipient: form.recipient_kind === 'member'
              ? { kind: 'member', member_id: form.recipient_member_id }
              : { kind: 'external', name: form.recipient_name.trim() },
            confirmer_member_id: form.recipient_kind === 'external' ? form.confirmer_member_id : undefined,
            issue_day: form.issue_day,
            due_day: form.due_day,
            due_month_offset: form.due_month_offset,
            start_month: form.start_month,
            end_month: form.end_month || null,
            reason: form.reason || undefined,
          })}
        >
          儲存
        </button>
      </div>
    </Modal>
  );
}
